import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import ListingCard from '../components/ListingCard';
import Filters from '../components/Filters';
import { CATEGORIES } from '../data/mock';
import { listingsApi } from '../api/client';
import { ChevronLeft, ChevronRight, ShieldCheck, Lock, EyeOff, Loader2, SlidersHorizontal, X } from 'lucide-react';

const PAGE_SIZE = 30;

const InseratePage = () => {
  const [sp, setSp] = useSearchParams();
  const location = useLocation();
  const [filters, setFilters] = useState({
    region: sp.get('region') || 'Alle Regionen',
    category: sp.get('category') || 'Alle',
    offer: '',
    query: sp.get('q') || '',
    near_city: sp.get('near_city') || '',
    near_lat: sp.get('near_lat') ? parseFloat(sp.get('near_lat')) : null,
    near_lng: sp.get('near_lng') ? parseFloat(sp.get('near_lng')) : null,
    radius_km: sp.get('radius_km') ? parseInt(sp.get('radius_km'), 10) : null,
  });
  const [page, setPage] = useState(parseInt(sp.get('page') || '1', 10));
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const didRestore = useRef(false);

  const goPage = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sync filters → URL
  useEffect(() => {
    const next = {};
    if (filters.region !== 'Alle Regionen') next.region = filters.region;
    if (filters.category !== 'Alle') next.category = filters.category;
    if (filters.query) next.q = filters.query;
    if (filters.near_city) next.near_city = filters.near_city;
    if (filters.near_lat !== null) next.near_lat = String(filters.near_lat);
    if (filters.near_lng !== null) next.near_lng = String(filters.near_lng);
    if (filters.radius_km) next.radius_km = String(filters.radius_km);
    if (page > 1) next.page = String(page);
    setSp(next, { replace: true });
  }, [filters, page]); // eslint-disable-line

  useEffect(() => { setPage(1); }, [filters.region, filters.category, filters.query, filters.offer, filters.near_city, filters.near_lat, filters.near_lng, filters.radius_km]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (filters.region !== 'Alle Regionen') params.region = filters.region;
    if (filters.category !== 'Alle') params.category = filters.category;
    if (filters.query) params.q = filters.query;
    if (filters.radius_km) {
      if (filters.near_lat !== null && filters.near_lng !== null) {
        params.near_lat = filters.near_lat;
        params.near_lng = filters.near_lng;
        params.radius_km = filters.radius_km;
      } else if (filters.near_city) {
        params.near_city = filters.near_city;
        params.radius_km = filters.radius_km;
      }
    }
    listingsApi.list(params)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setData({ items: [], total: 0, total_pages: 1 }); })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          // Restore scroll once per mount after initial listings load
          if (!didRestore.current) {
            didRestore.current = true;
            try {
              const y = sessionStorage.getItem('inserate_scroll_y');
              if (y) {
                setTimeout(() => window.scrollTo({ top: parseInt(y, 10), behavior: 'instant' in window ? 'instant' : 'auto' }), 30);
              }
              sessionStorage.removeItem('inserate_scroll_y');
            } catch {}
          }
        }
      });
    return () => { cancelled = true; };
  }, [filters, page]);

  const activeFilterCount =
    (filters.region !== 'Alle Regionen' ? 1 : 0) +
    (filters.category !== 'Alle' ? 1 : 0) +
    (filters.query ? 1 : 0) +
    (filters.radius_km && (filters.near_city || filters.near_lat !== null) ? 1 : 0);

  return (
    <div className="min-h-screen bg-[#0f0a0c]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-60"
          style={{ background: 'radial-gradient(ellipse at top left, #4a1f3a 0%, transparent 50%), radial-gradient(ellipse at bottom right, #1c2a4a 0%, transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-8 sm:pb-10">
          <p className="text-xs tracking-[0.3em] uppercase text-[#c19a8a] mb-3">Schweiz · 18+ · Diskret</p>
          <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
            Diskrete <span className="italic text-[#e8a3b8]">Inserate</span><br />
            in der ganzen Schweiz
          </h1>
          <p className="mt-4 sm:mt-5 text-sm sm:text-lg text-[#b8a9a3] max-w-2xl">
            Das Schweizer Portal für Erotik-Inserate — seriös, diskret & aktuell. {data.total} aktive Inserate.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-xs text-[#9a8a83]">
            <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#c19a8a]" /> Verschlüsselt</span>
            <span className="inline-flex items-center gap-1.5"><EyeOff className="w-3.5 h-3.5 text-[#c19a8a]" /> Kein Tracking</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#c19a8a]" /> Werbefrei</span>
          </div>
        </div>
      </section>

      {/* Category pills */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-5">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setFilters({ ...filters, category: c })}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-xs tracking-wide border transition-colors duration-200 ${
                filters.category === c
                  ? 'bg-[#a83a5b] border-[#a83a5b] text-white'
                  : 'bg-transparent border-[#2a1f24] text-[#b8a9a3] hover:border-[#a83a5b] hover:text-white'
              }`}>
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Filters area */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
        {/* Mobile toggle button */}
        <button onClick={() => setShowMobileFilters((v) => !v)}
          className="md:hidden w-full px-4 py-3 rounded-md bg-[#1a1417] border border-[#2a1f24] text-white inline-flex items-center justify-between mb-3">
          <span className="inline-flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-[#c19a8a]" />
            Filter & Suche
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-[#a83a5b] text-[10px] font-semibold">{activeFilterCount}</span>
            )}
          </span>
          <span className="text-xs text-[#7a6b65] inline-flex items-center gap-1">
            {data.total} Ergebnisse {showMobileFilters ? <X className="w-3 h-3" /> : <ChevronRight className="w-3 h-3 rotate-90" />}
          </span>
        </button>

        {/* Filters: desktop always visible, mobile toggleable in-place */}
        <div className={`${showMobileFilters ? 'block' : 'hidden'} md:block`}>
          <Filters filters={filters} setFilters={setFilters} resultCount={data.total} />
        </div>
      </section>

      {/* Listings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-baseline justify-between mb-5 pt-2">
          <h2 className="font-serif text-xl sm:text-2xl text-white">Alle Inserate</h2>
          <span className="text-sm text-[#7a6b65]">{data.total} Inserate</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#9a8a83]">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#2a1f24] rounded-xl">
            <p className="text-[#9a8a83]">Keine Inserate gefunden. Bitte passe deine Filter an.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {data.items.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}

        {data.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12 flex-wrap">
            <button onClick={() => goPage(Math.max(1, page - 1))} disabled={page === 1}
              className="px-3 py-2 rounded-md border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> Zurück
            </button>
            {Array.from({ length: data.total_pages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => goPage(p)}
                className={`w-9 h-9 rounded-md text-sm transition-colors ${
                  p === page ? 'bg-[#a83a5b] text-white' : 'border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b]'
                }`}>
                {p}
              </button>
            ))}
            <button onClick={() => goPage(Math.min(data.total_pages, page + 1))} disabled={page === data.total_pages}
              className="px-3 py-2 rounded-md border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1">
              Weiter <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default InseratePage;
